// @ts-strict-ignore
import * as React from 'react';
import {
    AllPatternTokens,
    AppPlatform,
    ChecklistAttachment,
    ChecklistItem,
    ChecklistItemPlacement,
    ChecklistItemReleaseTypes,
    ChecklistItemTemplate,
    ChecklistItemType,
    DefaultUserGroupId,
    IntegrationCapability,
    RelativeTimeTrigger,
    ReleaseStepType,
    RunwayResponseType,
    SubscriptionFeatureType,
} from '../types';
import Modal from 'antd/lib/modal/Modal';
import { checklistItemTypeForPlacement, isStepChecklistPlacement, patternTokenSyntaxValidation } from '../helpers';
import { allCases, Nullable, OrgComponentProps, StepRunComponentProps } from '../typescriptHelpers';
import { filterOptionLabel, formValidAndRequiredFieldsNotEmpty } from '../helpers/formHelpers';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../reducers/reducers';
import {
    ReleaseStepNewChecklistItemRequest,
    ReleaseStepUpdateChecklistItemRequest,
    tryUpdateAppSettingsV2,
} from '../actions/appSettings';
import { AppSettingsV2Updatable } from '../actions/updatable';
import {
    displayTextForAppPlatform,
    displayTextForChecklistPlacement,
    displayTextForReleaseStepType,
    displayTextForUserChecklistGroup,
} from '../helpers/displayHelpers';
import Select from 'antd/lib/select';
import useForm from 'antd/lib/form/hooks/useForm';
import Form, { useWatch } from 'antd/lib/form/Form';
import FormItem from 'antd/lib/form/FormItem';
import Checkbox from 'antd/lib/checkbox/Checkbox';
import Input from 'antd/lib/input';
import ChecklistItemImageUpload from './checklistItems/ChecklistItemImageUpload';
import { useChecklistImageUpload } from '@/utils/hooks/useChecklistImageUpload';
import {
    selectApp,
    selectAppIntegrations,
    selectApprovalItem,
    selectAppStoreType,
    selectChecklistItem,
    selectIsWorkflowsCIStepEnabled,
    selectOrgGroups,
    selectOrgLevelChecklistItem,
    selectOrgProviders,
    selectOrgUsers,
    selectRegressionItem,
    selectStepChecklistItem,
    selectStepChecklistItemForStepRun,
    selectStepConfigDataFromStepGraphs,
    selectUserAppDataByAppIdMap,
    selectWorkflowEnabledSteps,
} from '../selectors/selectors';
import MarkdownEditor from './HOCs/MarkdownEditor';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import Popover from 'antd/lib/popover';
import { PatternTokenHelperTextComponent } from './appSettings/PatternTokenHelperTextComponent';
import { antGrey, runwayColors } from './styles/globalStyles';
import Radio from 'antd/lib/radio';
import { globalMessage } from '../utils/globalMessage';
import { PlanRestrictedFeatureWrapper } from './planRestrictions/PlanRestrictedFeatureWrapper';
import { selectAppOrgUsers } from '@/selectors/memoizedSelectors';
import { useCallUpdatable, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import {
    CreateChecklistItemRequest,
    CreateOrgChecklistItemTemplateRequest,
    StepRunUpdatable,
    UpdateChecklistItemRequest,
    UpdateOrgChecklistItemTemplateRequest,
} from '@/actions/stepRun';
import {
    CreateChecklistItemTemplateRequest,
    StepConfigUpdatable,
    UpdateChecklistItemTemplateRequest,
} from '@/actions/stepConfig';
import { InlineStepConfigDescription } from '@/components/common/InlineStepConfigDescription';
import { InfoCircleOutlined } from '@ant-design/icons';
import Tooltip from 'antd/lib/tooltip';
import { RelativeTimeTriggerSettings } from './RelativeTimeTriggerSettings';
import { OrgUpdatable } from '@/actions/organization';

enum OwnerType {
    ApproverGroups = 'approverGroups',
    IndividualOwners = 'owners',
}

interface CreateEditChecklistItemProps extends OrgComponentProps {
    open: boolean;
    releaseStepTypes: ReleaseStepType[];
    placement: ChecklistItemPlacement;
    onCancel: () => void;
    onSuccess: () => void;
    isEditingItemWithId?: string;
    releaseId?: string;
    defaultReleaseStepTypeOrStepConfigId?: ReleaseStepType | string;
    releaseVersion?: string; // passed when creating a CLI from a release, used to allow for one-off CLIs on the release
    displayableReleaseVersion?: string;
    onRolloutMonitoringNeedsRefresh?: () => void;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
    releaseStepType: ReleaseStepType;
    appId?: string;
    isOrgLevelTemplate: boolean;
}

const CreateUpdateChecklistItemModal: React.FC<CreateEditChecklistItemProps> = (
    props: CreateEditChecklistItemProps,
) => {
    const { Option } = Select;
    const [formIsValid, setFormIsValid] = React.useState(false);
    const [form] = useForm();
    const releaseStepTypeOrStepConfigId = useWatch('releaseStepTypeOrStepConfigId', form);
    const isOneOffForVersion = useWatch('oneOffForVersion', form);
    const isTargetCompletionSettingsOpen = useWatch(['targetCompletion', 'enabled'], form);
    const isIncompleteReminderEnabled = useWatch('incompleteReminderEnabled', form);
    const isAutocompleteAtTargetCompletionEnabled = useWatch('autocompleteAtTargetCompletionEnabled', form);
    const isCompletionNotificationEnabled = useWatch('notifyEnabled', form);
    const description = useWatch('description', form);
    const applyToAllAppsPlatforms = useWatch('applyToAllAppsPlatforms', form);
    const applyToSelectionType = useWatch('applyToSelectionType', form);
    const userOwnerValues: string[] = useWatch(OwnerType.IndividualOwners, form);

    const dispatch = useDispatch();

    const [ownerType, setOwnerType] = React.useState(OwnerType.ApproverGroups);

    const { images, setImages, isUploadingImages, uploadNewImages, loadExistingAttachments } = useChecklistImageUpload({
        orgId: props.orgId,
        appId: props.appId,
    });

    const isStepChecklistItem = isStepChecklistPlacement(props.placement);

    let releaseStepTypes = props.releaseStepTypes;
    if (!releaseStepTypes.includes(ReleaseStepType.Rollout)) {
        releaseStepTypes = [...releaseStepTypes, ReleaseStepType.Rollout];
    }

    const workflowId = props.appId ? useSelector((state: RootState) => selectApp(state, props.appId).workflowId) : null;

    const workflowEnabledSteps =
        props.appId ? useSelector((state: RootState) => selectWorkflowEnabledSteps(state, props.appId)) : [];

    const appDataByAppIdMap = useSelector((state: RootState) => selectUserAppDataByAppIdMap(state, props.orgId));
    const allAvailableApps = Object.values(appDataByAppIdMap);
    const allAppIds = allAvailableApps.map((appData) => appData.appId);
    const allAppPlatforms = allCases(AppPlatform);

    // Selectors
    const isEditingItem = useSelector((state: RootState) => {
        if (props.isEditingItemWithId) {
            switch (props.placement) {
                case ChecklistItemPlacement.StepChecklist: {
                    if (props.appId) {
                        // first attempt to get the app CLI. if not present, can assume the CLI is one-off for this release, so just return the step's CLI
                        const appSettingsChecklistItem = selectChecklistItem(
                            state,
                            props.appId,
                            props.isEditingItemWithId,
                        );

                        if (appSettingsChecklistItem) {
                            return appSettingsChecklistItem;
                        } else if (props.releaseId && props.defaultReleaseStepTypeOrStepConfigId) {
                            const stepChecklistItem =
                                props.stepRunComponentProps ?
                                    selectStepChecklistItemForStepRun(
                                        state,
                                        props.stepRunComponentProps.stepRunId,
                                        props.isEditingItemWithId,
                                    )
                                :   selectStepChecklistItem(
                                        state,
                                        props.releaseId,
                                        props.defaultReleaseStepTypeOrStepConfigId as ReleaseStepType,
                                        props.isEditingItemWithId,
                                    );

                            return stepChecklistItem;
                        } else {
                            return null;
                        }
                    } else {
                        const orgSettingsChecklistItem = selectOrgLevelChecklistItem(state, props.isEditingItemWithId);

                        if (orgSettingsChecklistItem) {
                            return orgSettingsChecklistItem;
                        }
                        return null;
                    }
                }
                case ChecklistItemPlacement.Approvals:
                    return selectApprovalItem(
                        state,
                        props.releaseId,
                        props.isEditingItemWithId,
                        props.stepRunComponentProps?.stepRunId,
                    );
                case ChecklistItemPlacement.RegressionTesting:
                    return selectRegressionItem(
                        state,
                        props.releaseId,
                        props.isEditingItemWithId,
                        props.stepRunComponentProps?.stepRunId,
                    );
            }
        }

        return null;
    });

    const isWorkflowsEnabled = !!workflowId;

    const isStepConfigId = (value?: string) => !!value && !releaseStepTypes.includes(value as ReleaseStepType);
    const editingStepConfigId =
        (isEditingItem as ChecklistItemTemplate)?.stepConfigId ?? props.stepRunComponentProps?.stepConfigId;
    const selectedStepConfigId =
        isStepConfigId(releaseStepTypeOrStepConfigId) ? releaseStepTypeOrStepConfigId
        : isEditingItem ? editingStepConfigId
        : null;
    const shouldModifyChecklistTemplate = isWorkflowsEnabled && !!selectedStepConfigId;
    const [isWorkflowsEnabledStep, setIsWorkflowsEnabledStep] = React.useState(
        isWorkflowsEnabled && workflowEnabledSteps[props.releaseStepType],
    );
    React.useEffect(() => {
        const isReleaseStepType = Object.values(ReleaseStepType).includes(releaseStepTypeOrStepConfigId);
        setIsWorkflowsEnabledStep(isReleaseStepType ? !!workflowEnabledSteps[releaseStepTypeOrStepConfigId] : true);
    }, [releaseStepTypeOrStepConfigId, isWorkflowsEnabledStep]);

    const shouldModifyChecklistOneOff =
        !!props.stepRunComponentProps &&
        (isOneOffForVersion ||
            ((isEditingItem as ChecklistItem)?.stepRunId && !(isEditingItem as ChecklistItem).templateId));

    const stepGraphStepsMap =
        props.appId ? useSelector((state: RootState) => selectStepConfigDataFromStepGraphs(state, props.appId)) : [];

    const [
        createStepRunChecklistItem,
        createStepRunChecklistItemIsLoading,
        createStepRunChecklistItemResponseType,
        createStepRunChecklistItemError,
    ] = useCallUpdatable(StepRunUpdatable.CreateStepRunChecklistItem, props.stepRunComponentProps);

    const [
        createChecklistItemTemplate,
        createChecklistItemTemplateIsLoading,
        createChecklistItemTemplateResponseType,
        createChecklistItemTemplateError,
    ] = useCallUpdatable(StepConfigUpdatable.CreateChecklistItemTemplate, {
        orgId: props.orgId,
        workflowId: workflowId,
        stepConfigId: selectedStepConfigId,
    });

    const [
        updateChecklistItemOrgTemplate,
        updateChecklistItemOrgTemplateIsLoading,
        updateChecklistItemOrgTemplateResponseType,
        updateChecklistItemOrgTemplateError,
    ] = useCallUpdatable(OrgUpdatable.UpdateOrgChecklistItemTemplate, { orgId: props.orgId });

    const [
        createChecklistItemOrgTemplate,
        createChecklistItemOrgTemplateIsLoading,
        createChecklistItemOrgTemplateResponseType,
        createChecklistItemOrgTemplateError,
    ] = useCallUpdatable(OrgUpdatable.CreateOrgChecklistItemTemplate, { orgId: props.orgId });

    const releaseStepNewChecklistItemSaveIsLoading = useIsLoading(AppSettingsV2Updatable.NewChecklistItem);
    const releaseStepNewChecklistItemSaveCompleted = useUpdated(AppSettingsV2Updatable.NewChecklistItem);
    const releaseStepNewChecklistItemError = useError(AppSettingsV2Updatable.NewChecklistItem);

    const newChecklistItemSaveIsLoading =
        props.isOrgLevelTemplate ? createChecklistItemOrgTemplateIsLoading
        : shouldModifyChecklistOneOff ? createStepRunChecklistItemIsLoading
        : shouldModifyChecklistTemplate ? createChecklistItemTemplateIsLoading
        : releaseStepNewChecklistItemSaveIsLoading;
    const newChecklistItemSaveCompleted =
        props.isOrgLevelTemplate ? createChecklistItemOrgTemplateResponseType === RunwayResponseType.Success
        : shouldModifyChecklistOneOff ? createStepRunChecklistItemResponseType === RunwayResponseType.Success
        : shouldModifyChecklistTemplate ? createChecklistItemTemplateResponseType === RunwayResponseType.Success
        : releaseStepNewChecklistItemSaveCompleted;
    const newChecklistItemError =
        props.isOrgLevelTemplate ? createChecklistItemOrgTemplateError
        : shouldModifyChecklistOneOff ? createStepRunChecklistItemError
        : shouldModifyChecklistTemplate ? createChecklistItemTemplateError
        : releaseStepNewChecklistItemError;

    const platform =
        props.appId ? useSelector((state: RootState) => selectApp(state, props.appId).appSettings.platform) : null;
    const appStoreType = props.appId ? useSelector((state: RootState) => selectAppStoreType(state, props.appId)) : null;

    const checklistItem = isEditingItem as ChecklistItem;

    const [
        updateStepRunChecklistItem,
        updateStepRunChecklistItemIsLoading,
        updateStepRunChecklistItemResponseType,
        updateStepRunChecklistItemError,
    ] = useCallUpdatable(
        StepRunUpdatable.UpdateStepRunChecklistItem,
        {
            checklistItemId: props.isEditingItemWithId,
            ...props.stepRunComponentProps,
        },
        props.isEditingItemWithId,
    );

    const [
        updateChecklistItemTemplate,
        updateChecklistItemTemplateIsLoading,
        updateChecklistItemTemplateResponseType,
        updateChecklistItemTemplateError,
    ] = useCallUpdatable(StepConfigUpdatable.UpdateChecklistItemTemplate, {
        orgId: props.orgId,
        workflowId: workflowId,
        stepConfigId: selectedStepConfigId,
        checklistItemId: checklistItem?.templateId ?? checklistItem?.uid,
    });

    const releaseStepIsUpdateLoading = useIsLoading(AppSettingsV2Updatable.UpdateReleaseStepChecklistItem);
    const releaseStepIsUpdated = useUpdated(AppSettingsV2Updatable.UpdateReleaseStepChecklistItem);
    const releaseStepUpdateError = useError(AppSettingsV2Updatable.UpdateReleaseStepChecklistItem);

    const isUpdateLoading =
        props.isOrgLevelTemplate ? updateChecklistItemOrgTemplateIsLoading
        : shouldModifyChecklistOneOff ? updateStepRunChecklistItemIsLoading
        : shouldModifyChecklistTemplate ? updateChecklistItemTemplateIsLoading
        : releaseStepIsUpdateLoading;
    const isUpdated =
        props.isOrgLevelTemplate ? updateChecklistItemOrgTemplateResponseType === RunwayResponseType.Success
        : shouldModifyChecklistOneOff ? updateStepRunChecklistItemResponseType === RunwayResponseType.Success
        : shouldModifyChecklistTemplate ? updateChecklistItemTemplateResponseType === RunwayResponseType.Success
        : releaseStepIsUpdated;
    const updateError =
        props.isOrgLevelTemplate ? updateChecklistItemOrgTemplateError
        : shouldModifyChecklistOneOff ? updateStepRunChecklistItemError
        : shouldModifyChecklistTemplate ? updateChecklistItemTemplateError
        : releaseStepUpdateError;

    const users =
        props.appId ?
            useSelector((state: RootState) => selectAppOrgUsers(state, props.appId))
        :   useSelector((state: RootState) => selectOrgUsers(state, props.orgId));

    const notificationsProvider = useSelector((state: RootState) => {
        if (!props.appId) return null;
        const integrations = selectAppIntegrations(state, props.appId);
        const notifIntegration = integrations.find((i) => i.capabilities.includes(IntegrationCapability.Notifications));
        if (notifIntegration) {
            const providers = selectOrgProviders(state, props.orgId);
            return providers.find((p) => p.id === notifIntegration.providerId);
        }
        return null;
    });

    const availableChannels = notificationsProvider?.data?.availableChannels || [];

    // special case for editing a CLI that has pilot as an approver role and has been set with the current release's pilot as an owner
    const isEditingItemWithPilotApproverGroup =
        props.releaseId &&
        isEditingItem &&
        isEditingItem.approverGroups?.includes(DefaultUserGroupId.Pilot) &&
        (isEditingItem.owners?.length ?? 0) > 0;

    // Functions

    const createOrUpdateChecklistItem = (
        title: string,
        notifyEnabled: boolean,
        releaseTypes: ChecklistItemReleaseTypes,
        placement: ChecklistItemPlacement,
        approverGroups: string[],
        owners: string[],
        incompleteReminderEnabled: boolean,
        autocompleteAtTargetCompletionEnabled: boolean,
        includeDescriptionInNotificationsEnabled: boolean,
        disableReminderAfterReleaseComplete: boolean,
        releaseStepType?: ReleaseStepType,
        description?: string,
        editingItemId?: string,
        type?: ChecklistItemType,
        oneOffForVersion?: string,
        targetCompletion?: RelativeTimeTrigger,
        applyToAllAppsPlatforms?: boolean,
        apps?: string[],
        platformTypes?: AppPlatform[],
        channels?: string[],
        attachments?: ChecklistAttachment[],
    ) => {
        if (editingItemId) {
            const requestData: ReleaseStepUpdateChecklistItemRequest = {
                orgId: props.orgId,
                appId: props.appId,
                checklistItemId: editingItemId,
                title,
                notifyEnabled,
                description,
                approverGroups: approverGroups,
                owners,
                releaseTypes,
                oneOffForVersion,
                targetCompletion,
                incompleteReminderEnabled,
                autocompleteAtTargetCompletionEnabled,
                includeDescriptionInNotificationsEnabled,
                disableReminderAfterReleaseComplete,
                channels,
                attachments,
            };

            if (props.isOrgLevelTemplate) {
                const data: UpdateOrgChecklistItemTemplateRequest = {
                    ...requestData,
                    type,
                    placement,
                    checklistItemId: props.isEditingItemWithId,
                    shouldApplyToAllAppsAndPlatforms: applyToAllAppsPlatforms,
                    apps: apps,
                    platforms: platformTypes,
                };
                updateChecklistItemOrgTemplate(data);
            } else if (shouldModifyChecklistOneOff) {
                const data: UpdateChecklistItemRequest = {
                    ...requestData,
                    type,
                    placement,
                };
                updateStepRunChecklistItem(data);
            } else if (shouldModifyChecklistTemplate) {
                const data: UpdateChecklistItemTemplateRequest = {
                    ...requestData,
                    type,
                    placement,
                    originWorkflowRunId: props.stepRunComponentProps?.workflowRunId,
                    originStepRunId: props.stepRunComponentProps?.stepRunId,
                };
                updateChecklistItemTemplate(data);
            } else {
                dispatch(tryUpdateAppSettingsV2(AppSettingsV2Updatable.UpdateReleaseStepChecklistItem, requestData));
            }
        } else {
            const requestData: ReleaseStepNewChecklistItemRequest = {
                orgId: props.orgId,
                appId: props.appId,
                title,
                notifyEnabled,
                type,
                placement,
                releaseStepType,
                description,
                approverGroups: approverGroups,
                owners,
                releaseTypes,
                oneOffForVersion,
                releaseId: props.releaseId,
                targetCompletion,
                incompleteReminderEnabled,
                autocompleteAtTargetCompletionEnabled,
                includeDescriptionInNotificationsEnabled,
                disableReminderAfterReleaseComplete,
                channels,
                attachments,
            };

            if (props.isOrgLevelTemplate) {
                const data: CreateOrgChecklistItemTemplateRequest = {
                    ...requestData,
                    orgId: props.orgId,
                    shouldApplyToAllAppsAndPlatforms: applyToAllAppsPlatforms,
                    apps: apps,
                    platforms: platformTypes,
                };
                createChecklistItemOrgTemplate(data);
            } else if (shouldModifyChecklistOneOff) {
                const data: CreateChecklistItemRequest = {
                    ...requestData,
                };
                createStepRunChecklistItem(data);
            } else if (shouldModifyChecklistTemplate) {
                const data: CreateChecklistItemTemplateRequest = {
                    ...requestData,
                    originWorkflowRunId: props.stepRunComponentProps?.workflowRunId,
                    originStepRunId: props.stepRunComponentProps?.stepRunId,
                };
                createChecklistItemTemplate(data);
            } else {
                dispatch(tryUpdateAppSettingsV2(AppSettingsV2Updatable.NewChecklistItem, requestData));
            }
        }
    };
    const onFieldsChange = () => {
        const requiredFields = ['title'];

        if (isStepChecklistItem && !shouldModifyChecklistOneOff) {
            requiredFields.push('releaseStepTypeOrStepConfigId');
        }
        const isValid = formValidAndRequiredFieldsNotEmpty(form, requiredFields);
        setFormIsValid(isValid);
    };

    const onFinish = () => {
        form.validateFields()
            .then(async (values) => {
                const description = values.description;
                const title = values.title;
                const approverGroups = values.approverGroups;
                const owners = values.owners;
                const editingItemId = props.isEditingItemWithId;
                const releaseTypes = values.releaseTypes;
                const notifyEnabled = values.notifyEnabled;
                const placement = props.placement;
                const itemType = checklistItemTypeForPlacement(props.placement);
                const oneOffForVersion = values.oneOffForVersion === true ? props.releaseVersion : null;
                const incompleteReminderEnabled = values.incompleteReminderEnabled;
                const autocompleteAtTargetCompletionEnabled = values.autocompleteAtTargetCompletionEnabled;
                const includeDescriptionInNotificationsEnabled = values.includeDescriptionInNotificationsEnabled;
                const disableReminderAfterReleaseComplete = values.disableReminderAfterReleaseComplete;
                const targetCompletion = values.targetCompletion;
                const channels = values.channels;
                const shouldApplyToAllAppsPlatforms = values.applyToAllAppsPlatforms ?? true;
                const appsToInclude =
                    shouldApplyToAllAppsPlatforms ? allAppIds
                    : !props.isOrgLevelTemplate || values.applyToSelectionType !== 'apps' ? []
                    : (values.apps ?? []);
                const platformsToInclude =
                    shouldApplyToAllAppsPlatforms ? allAppPlatforms
                    : !props.isOrgLevelTemplate || values.applyToSelectionType !== 'platforms' ? []
                    : (values.platformTypes ?? []);

                const uploadedAttachments = await uploadNewImages();

                createOrUpdateChecklistItem(
                    title,
                    notifyEnabled,
                    releaseTypes,
                    placement,
                    approverGroups,
                    owners,
                    incompleteReminderEnabled,
                    autocompleteAtTargetCompletionEnabled,
                    includeDescriptionInNotificationsEnabled,
                    disableReminderAfterReleaseComplete,
                    values.releaseStepTypeOrStepConfigId,
                    description,
                    editingItemId,
                    itemType,
                    oneOffForVersion,
                    targetCompletion,
                    shouldApplyToAllAppsPlatforms,
                    appsToInclude,
                    platformsToInclude,
                    channels,
                    uploadedAttachments,
                );
            })
            .catch((error) => {
                globalMessage.error(error);

                setFormIsValid(false);
            });
    };

    const handleOwnerSelections = (field) => {
        const fieldValues = form.getFieldValue(field);

        const otherField = field === OwnerType.ApproverGroups ? OwnerType.IndividualOwners : OwnerType.ApproverGroups;
        const otherFieldValues = form.getFieldValue(otherField);

        // //if user set values for field, remove selections from other field
        if (fieldValues?.length > 0 && otherFieldValues?.length > 0) {
            form.setFieldsValue({ [otherField]: [] });
        }
    };

    const isWorkflowsCIStepEnabled =
        props.appId ? useSelector((state: RootState) => selectIsWorkflowsCIStepEnabled(state, props.appId)) : true;

    const placementText = displayTextForChecklistPlacement(props.placement).toLocaleLowerCase();
    const title = isEditingItem ? `Edit ${placementText} item` : `New ${placementText} item`;
    const buttonTitle = isEditingItem ? `Update ${placementText} item` : `Create ${placementText} item`;
    const helperText = `${
        isEditingItem ? 'Changes to t' : 'T'
    }his checklist item will apply to all releases for the app.`;
    const allGroups = useSelector((state: RootState) => selectOrgGroups(state, props.orgId));
    const groupNameById = React.useMemo(() => {
        return Object.entries(allGroups).reduce(
            (acc, [groupId, group]) => {
                acc[groupId] = group?.name?.toLowerCase() ?? '';
                return acc;
            },
            {} as Record<string, string>,
        );
    }, [allGroups]);

    const ownerGroupOptions = React.useMemo(() => {
        return Object.keys(allGroups)
            .filter((userGroupId) => !allGroups[userGroupId]?.disabled)
            .map((userGroupId) => ({
                value: userGroupId,
                label: displayTextForUserChecklistGroup(allGroups[userGroupId]),
            }));
    }, [allGroups]);

    const ownerUserOptions = React.useMemo(() => {
        return (users ?? []).map((user) => ({
            value: user.email,
            label: user.email,
        }));
    }, [users]);

    const migratedReleaseArchetypes = React.useMemo(() => {
        return new Set(Object.values(stepGraphStepsMap).map((stepGraphStep) => stepGraphStep.archetype));
    }, [stepGraphStepsMap]);

    const releaseStepOptions = React.useMemo(() => {
        return releaseStepTypes.filter((releaseStepType) => {
            // filter any migrated release steps only if stepGraphStepsMap contains at least one step with that archetype
            return !(
                isWorkflowsEnabled &&
                workflowEnabledSteps[releaseStepType] &&
                migratedReleaseArchetypes.has(releaseStepType)
            );
        });
    }, [releaseStepTypes, isWorkflowsEnabled, workflowEnabledSteps, migratedReleaseArchetypes]);

    const stepConfigOptions = React.useMemo(() => {
        if (!isWorkflowsEnabled) {
            return [];
        }

        return Object.entries(stepGraphStepsMap).filter(([, stepGraphStep]) => {
            // only show release steps that are migrated
            return workflowEnabledSteps[stepGraphStep.archetype];
        });
    }, [stepGraphStepsMap, isWorkflowsEnabled, workflowEnabledSteps]);

    const isEditing = !!isEditingItem;
    const isEditingOrgLevelTemplate = props.isOrgLevelTemplate && isEditing;
    const editingTemplateApps = isEditingOrgLevelTemplate ? ((isEditingItem as ChecklistItemTemplate).apps ?? []) : [];
    const editingTemplatePlatforms =
        isEditingOrgLevelTemplate ? ((isEditingItem as ChecklistItemTemplate).platforms ?? []) : [];
    const templateShouldApplyToAllAppsPlatforms =
        isEditingOrgLevelTemplate ?
            ((isEditingItem as ChecklistItemTemplate).shouldApplyToAllAppsAndPlatforms ?? true)
        :   undefined;
    const applyToAllAppsPlatformsValue =
        props.isOrgLevelTemplate ?
            (applyToAllAppsPlatforms ?? templateShouldApplyToAllAppsPlatforms ?? true)
        :   applyToAllAppsPlatforms;
    const applyToSelectionTypeValue =
        props.isOrgLevelTemplate ?
            (applyToSelectionType ??
            (editingTemplateApps.length > 0 ? 'apps'
            : editingTemplatePlatforms.length > 0 ? 'platforms'
            : 'apps'))
        :   applyToSelectionType;
    const releaseStepTypeShouldFocus = !isEditing && !props.defaultReleaseStepTypeOrStepConfigId && isStepChecklistItem;
    const checklistNameShouldFocus = !!props.defaultReleaseStepTypeOrStepConfigId;

    const tooltipHelpStyling: React.CSSProperties = {
        paddingLeft: 0,
        fontSize: '12px',
        paddingBottom: 0,
        marginBottom: 0,
        marginRight: 0,
    };

    const bulletPointHelpStyling: React.CSSProperties = {
        paddingLeft: 16,
        fontSize: '12px',
        paddingBottom: 0,
        marginBottom: 0,
        marginTop: 10,
        color: runwayColors.statusGrey,
    };

    const initialValues =
        isEditingItem ?
            {
                releaseStepTypeOrStepConfigId:
                    (isEditingItem as ChecklistItemTemplate).stepConfigId ??
                    props.stepRunComponentProps?.stepConfigId ??
                    isEditingItem.releaseStepType ??
                    props.defaultReleaseStepTypeOrStepConfigId,
                title: isEditingItem.title ?? null,
                description: isEditingItem.description ?? '',
                approverGroups: isEditingItem.approverGroups ?? [],
                // do not populate owners for cases where approver group is pilot and we're editing a release's CLI
                owners: isEditingItemWithPilotApproverGroup ? [] : (isEditingItem.owners ?? []),
                notifyEnabled: isEditingItem.notifyEnabled ?? true,
                releaseTypes: isEditingItem.releaseTypes ?? ChecklistItemReleaseTypes.All,
                oneOffForVersion: !!isEditingItem.oneOffForVersion || shouldModifyChecklistOneOff,
                // incompleteReminderEnabled and autocompleteAtTargetCompletionEnabled are mutually exclusive
                // This behavior is being introduced now, previously we could have both enabled, so we need to handle the case where both are true
                incompleteReminderEnabled:
                    isEditingItem.incompleteReminderEnabled && !isEditingItem.autocompleteAtTargetCompletionEnabled,
                autocompleteAtTargetCompletionEnabled: isEditingItem?.autocompleteAtTargetCompletionEnabled,
                includeDescriptionInNotificationsEnabled: isEditingItem?.includeDescriptionInNotificationsEnabled,
                disableReminderAfterReleaseComplete: isEditingItem?.disableReminderAfterReleaseComplete ?? false,
                targetCompletion: isEditingItem?.targetCompletion ?? { enabled: false },
                apps: props.isOrgLevelTemplate ? ((isEditingItem as ChecklistItemTemplate).apps ?? []) : undefined,
                platformTypes:
                    props.isOrgLevelTemplate ? ((isEditingItem as ChecklistItemTemplate).platforms ?? []) : undefined,
                applyToAllAppsPlatforms:
                    !props.isOrgLevelTemplate ||
                    ((isEditingItem as ChecklistItemTemplate).shouldApplyToAllAppsAndPlatforms ?? true),
                applyToSelectionType:
                    props.isOrgLevelTemplate && ((isEditingItem as ChecklistItemTemplate).apps ?? []).length > 0 ?
                        'apps'
                    : (
                        props.isOrgLevelTemplate &&
                        ((isEditingItem as ChecklistItemTemplate).platforms ?? []).length > 0
                    ) ?
                        'platforms'
                    :   'apps',
            }
        :   {
                releaseStepTypeOrStepConfigId: props.defaultReleaseStepTypeOrStepConfigId ?? null,
                releaseTypes: ChecklistItemReleaseTypes.All,
                title: null,
                description: null,
                approverGroups: [DefaultUserGroupId.Pilot],
                owners: [],
                notifyEnabled: true,
                oneOffForVersion: false,
                incompleteReminderEnabled: false,
                autocompleteAtTargetCompletionEnabled: false,
                includeDescriptionInNotificationsEnabled: false,
                disableReminderAfterReleaseComplete: false,
                targetCompletion: { enabled: false },
                applyToAllAppsPlatforms: true,
                applyToSelectionType: 'apps',
            };

    const hasNonPilotOwners =
        (initialValues.owners?.length ?? 0) > 0 && !initialValues.approverGroups?.includes(DefaultUserGroupId.Pilot);

    const ownersLabel = (
        <Space>
            Owners
            <Tooltip title="In addition to any specified owners / owner groups, anyone with a default or custom role that has the 'Complete/uncomplete checklist items' permission will be able to update the status on any checklist item.">
                <InfoCircleOutlined style={{ color: runwayColors.primary }} />
            </Tooltip>
        </Space>
    );

    // Effects
    React.useEffect(() => {
        form.setFieldsValue({ releaseStepType: props.defaultReleaseStepTypeOrStepConfigId ?? null });
    }, [props.defaultReleaseStepTypeOrStepConfigId]);

    React.useEffect(() => {
        if (newChecklistItemSaveCompleted) {
            globalMessage.success('New checklist item added!');

            props.onSuccess();
        }

        if (props.onRolloutMonitoringNeedsRefresh) {
            props.onRolloutMonitoringNeedsRefresh();
        }
    }, [newChecklistItemSaveCompleted]);

    React.useEffect(() => {
        if (newChecklistItemError) {
            globalMessage.error(newChecklistItemError);
        }
    }, [newChecklistItemError]);

    React.useEffect(() => {
        if (isUpdated) {
            globalMessage.success('Updated!');

            props.onSuccess();
        }

        if (props.onRolloutMonitoringNeedsRefresh) {
            props.onRolloutMonitoringNeedsRefresh();
        }
    }, [isUpdated]);

    React.useEffect(() => {
        if (updateError) {
            globalMessage.error(updateError);
        }
    }, [updateError]);

    React.useEffect(() => {
        if (props.open) {
            form.setFieldsValue(initialValues);

            if (hasNonPilotOwners) {
                // set radio to individual owners
                form.setFieldValue('ownerType', OwnerType.IndividualOwners);
                setOwnerType(OwnerType.IndividualOwners);
            }

            loadExistingAttachments(isEditingItem?.attachments);
        }
    }, [props.open]);

    return (
        <Modal
            title={title}
            open={props.open}
            onCancel={props.onCancel}
            onOk={() => form.submit()}
            okText={buttonTitle}
            confirmLoading={newChecklistItemSaveIsLoading || isUpdateLoading || isUploadingImages}
            okButtonProps={{ disabled: !formIsValid }}
            afterClose={() => {
                form.resetFields();
                setFormIsValid(false);
                setOwnerType(OwnerType.ApproverGroups);
            }}
            destroyOnClose={true}
            width="70%"
            style={{ minWidth: 500, maxWidth: 800, top: 48 }}
        >
            <Form
                form={form}
                name="newChecklistItem"
                onFinish={onFinish}
                onFieldsChange={onFieldsChange}
                autoComplete="off"
                layout="vertical"
                initialValues={initialValues}
            >
                <div style={{ display: 'flex', flexWrap: 'wrap', width: '100%', columnGap: 24 }}>
                    <FormItem
                        hidden={!isStepChecklistItem}
                        label="Release step"
                        name="releaseStepTypeOrStepConfigId"
                        rules={[{ required: true }]}
                        style={{ minWidth: 280, width: '100%', flex: 1 }}
                    >
                        <Select placeholder="Select" disabled={isEditing} autoFocus={releaseStepTypeShouldFocus}>
                            {releaseStepOptions.map((releaseStepType) => {
                                return (
                                    <Option key={releaseStepType} value={releaseStepType}>
                                        {displayTextForReleaseStepType(
                                            releaseStepType,
                                            platform,
                                            appStoreType,
                                            isWorkflowsCIStepEnabled,
                                        )}
                                    </Option>
                                );
                            })}
                            {stepConfigOptions.map(([stepConfigId, stepGraphStep]) => {
                                return (
                                    <Option key={stepConfigId} value={stepConfigId}>
                                        <InlineStepConfigDescription
                                            stepGraphStep={stepGraphStep}
                                            platform={platform}
                                            appStoreType={appStoreType}
                                            isWorkflowsCIStepEnabled={isWorkflowsCIStepEnabled}
                                        />
                                    </Option>
                                );
                            })}
                        </Select>
                    </FormItem>
                    <FormItem
                        label="Release types"
                        name="releaseTypes"
                        style={{ minWidth: 280, width: '100%', flex: 1 }}
                    >
                        <Select>
                            <Option key="all" value={ChecklistItemReleaseTypes.All}>
                                All types
                            </Option>
                            <Option key="allExceptHotfix" value={ChecklistItemReleaseTypes.AllExceptHotfixAndRollback}>
                                All types except hotfixes and rollbacks
                            </Option>
                            <Option key="hotfix" value={ChecklistItemReleaseTypes.HotfixOnly}>
                                Hotfixes only
                            </Option>
                            <Option key="rollback" value={ChecklistItemReleaseTypes.RollbackOnly}>
                                Rollbacks only
                            </Option>
                        </Select>
                    </FormItem>
                </div>
                {props.isOrgLevelTemplate && (
                    <div style={{ marginBottom: 12 }}>
                        <FormItem name="applyToAllAppsPlatforms" valuePropName="checked" style={{ marginBottom: 8 }}>
                            <Checkbox
                                onChange={(event) => {
                                    form.setFieldsValue(
                                        event.target.checked ?
                                            { apps: allAppIds, platformTypes: allAppPlatforms }
                                        :   { apps: [], platformTypes: [] },
                                    );
                                }}
                            >
                                Apply to all apps on all platforms
                            </Checkbox>
                        </FormItem>
                        {!applyToAllAppsPlatformsValue && (
                            <FormItem name="applyToSelectionType" style={{ marginBottom: 8 }}>
                                <Radio.Group
                                    onChange={(event) => {
                                        form.setFieldsValue({
                                            applyToSelectionType: event.target.value,
                                            apps: [],
                                            platformTypes: [],
                                        });
                                    }}
                                >
                                    <Radio value="apps">
                                        Apply only to selected apps
                                        <Tooltip title="New apps will not conform to this checklist item, unless manually added">
                                            <InfoCircleOutlined style={{ marginLeft: 6, color: runwayColors.info }} />
                                        </Tooltip>
                                    </Radio>
                                    <Radio value="platforms">
                                        Apply only to selected platforms
                                        <Tooltip title="New apps added of this platform type will conform to this checklist item">
                                            <InfoCircleOutlined style={{ marginLeft: 6, color: runwayColors.info }} />
                                        </Tooltip>
                                    </Radio>
                                </Radio.Group>
                            </FormItem>
                        )}
                        <div style={{ display: 'flex', flexWrap: 'wrap', width: '100%', columnGap: 24 }}>
                            <FormItem
                                hidden={applyToAllAppsPlatformsValue || applyToSelectionTypeValue !== 'apps'}
                                label="App(s)"
                                name="apps"
                                required={!applyToAllAppsPlatformsValue && applyToSelectionTypeValue === 'apps'}
                                rules={[
                                    {
                                        required: !applyToAllAppsPlatformsValue && applyToSelectionTypeValue === 'apps',
                                        message: 'Select at least one app.',
                                    },
                                ]}
                                style={{ minWidth: 280, width: '100%', flex: 1 }}
                            >
                                <Select
                                    placeholder="Select"
                                    mode="multiple"
                                    allowClear
                                    autoFocus={releaseStepTypeShouldFocus}
                                    dropdownRender={(options) => (
                                        <>
                                            <div style={{ padding: '6px 8px' }}>
                                                <Text
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => form.setFieldsValue({ apps: allAppIds })}
                                                >
                                                    Select all apps
                                                </Text>
                                            </div>
                                            {options}
                                        </>
                                    )}
                                >
                                    {allAvailableApps.map((appData) => {
                                        return (
                                            <Option value={appData.appId} key={appData.appId}>
                                                {appData.appName}{' '}
                                                <Text type="secondary">
                                                    ({displayTextForAppPlatform(appData.platform, appData.appStoreType)}
                                                    )
                                                </Text>
                                            </Option>
                                        );
                                    })}
                                </Select>
                            </FormItem>
                            <FormItem
                                hidden={applyToAllAppsPlatformsValue || applyToSelectionTypeValue !== 'platforms'}
                                label="Platform type(s)"
                                name="platformTypes"
                                required={!applyToAllAppsPlatformsValue && applyToSelectionTypeValue === 'platforms'}
                                rules={[
                                    {
                                        required:
                                            !applyToAllAppsPlatformsValue && applyToSelectionTypeValue === 'platforms',
                                        message: 'Select at least one platform.',
                                    },
                                ]}
                                style={{ minWidth: 280, width: '100%', flex: 1 }}
                            >
                                <Select
                                    placeholder="Select"
                                    mode="multiple"
                                    allowClear
                                    dropdownRender={(options) => (
                                        <>
                                            <div style={{ padding: '6px 8px' }}>
                                                <Text
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() =>
                                                        form.setFieldsValue({ platformTypes: allAppPlatforms })
                                                    }
                                                >
                                                    Select all platform types
                                                </Text>
                                            </div>
                                            {options}
                                        </>
                                    )}
                                >
                                    {allAppPlatforms.map((platform) => {
                                        return (
                                            <Option key={platform} value={platform}>
                                                {displayTextForAppPlatform(platform, appStoreType)}
                                            </Option>
                                        );
                                    })}
                                </Select>
                            </FormItem>
                        </div>
                    </div>
                )}
                <FormItem label="Title" name="title" rules={[{ required: true }]}>
                    <Input
                        placeholder="e.g. Update Confluence with latest details"
                        autoFocus={checklistNameShouldFocus}
                        autoComplete="off"
                    />
                </FormItem>
                {props.releaseVersion && (
                    <div style={{ marginBottom: 10 }}>
                        <FormItem
                            name="oneOffForVersion"
                            rules={[{ required: true }]}
                            valuePropName="checked"
                            style={{ marginBottom: 0 }}
                        >
                            <Checkbox disabled={!!isEditingItem && shouldModifyChecklistOneOff}>
                                Include this checklist item for this release ({props.displayableReleaseVersion}) only
                            </Checkbox>
                        </FormItem>
                    </div>
                )}
                <>
                    <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center' }}>
                        <div>
                            <Tooltip>
                                <FormItem
                                    name={['targetCompletion', 'enabled']}
                                    valuePropName="checked"
                                    style={{ padding: 0, margin: 0 }}
                                >
                                    <Checkbox> Set a target completion time </Checkbox>
                                </FormItem>
                            </Tooltip>
                        </div>
                        <Tooltip
                            overlayInnerStyle={{ marginRight: 0 }}
                            title={
                                <ul style={tooltipHelpStyling}>
                                    Set a target completion time relative to lifecycle events.
                                </ul>
                            }
                        >
                            <InfoCircleOutlined style={{ color: runwayColors.primary }} />
                        </Tooltip>
                    </div>
                    {isTargetCompletionSettingsOpen && (
                        <div style={{ marginBottom: 6 }}>
                            <RelativeTimeTriggerSettings
                                parentFieldName="targetCompletion"
                                orgId={props.orgId}
                                appId={props.appId}
                                stepConfigId={props.stepRunComponentProps?.stepConfigId}
                                initialValues={initialValues.targetCompletion}
                                isOrgLevelItem={props.isOrgLevelTemplate}
                            />
                            <div style={{ marginTop: 12, marginBottom: 10, display: 'flex', alignItems: 'center' }}>
                                <div>
                                    <Tooltip>
                                        <FormItem
                                            name="autocompleteAtTargetCompletionEnabled"
                                            valuePropName="checked"
                                            noStyle
                                        >
                                            <Checkbox disabled={isIncompleteReminderEnabled}>
                                                Auto-complete at target completion time
                                            </Checkbox>
                                        </FormItem>
                                    </Tooltip>
                                </div>
                            </div>
                            {isAutocompleteAtTargetCompletionEnabled && (
                                <ul
                                    style={{
                                        ...bulletPointHelpStyling,
                                        marginTop: 10,
                                        marginBottom: 10,
                                    }}
                                >
                                    <li>
                                        Runway will automatically update status to 'Completed' at the target completion
                                        time. Use this option where no action is needed. If you wish to receive a
                                        completion notification, select 'Send a completion notification' below.
                                    </li>
                                </ul>
                            )}
                            <div style={{ marginTop: 0, marginBottom: 0, display: 'flex', alignItems: 'center' }}>
                                <div>
                                    <Tooltip>
                                        <FormItem name="incompleteReminderEnabled" valuePropName="checked" noStyle>
                                            <Checkbox disabled={isAutocompleteAtTargetCompletionEnabled}>
                                                Send a reminder if incomplete
                                            </Checkbox>
                                        </FormItem>
                                    </Tooltip>
                                </div>
                                <Tooltip
                                    overlayInnerStyle={{ marginRight: 0 }}
                                    title={
                                        <ul style={tooltipHelpStyling}>
                                            Sent if the target completion time has passed and the checklist item has not
                                            yet been marked as complete
                                        </ul>
                                    }
                                >
                                    <InfoCircleOutlined style={{ color: runwayColors.primary }} />
                                </Tooltip>
                            </div>
                            <FormItem
                                shouldUpdate={(prev, curr) => prev.reminderEnabled !== curr.reminderEnabled}
                                noStyle
                            >
                                {({ getFieldValue }) =>
                                    getFieldValue('incompleteReminderEnabled') ?
                                        <ul style={bulletPointHelpStyling}>
                                            <li>
                                                Requires 'Checklist item is due' notification to be enabled in
                                                Notification settings.
                                            </li>
                                        </ul>
                                    :   null
                                }
                            </FormItem>

                            {isIncompleteReminderEnabled && (
                                <div
                                    style={{
                                        marginTop: 0,
                                        fontWeight: 'lighter',
                                        fontSize: 12,
                                        paddingLeft: 14,
                                        marginLeft: 0,
                                        color: antGrey.grey7,
                                    }}
                                ></div>
                            )}
                        </div>
                    )}
                </>

                <div style={{ marginTop: 0, marginBottom: 0, display: 'flex', alignItems: 'center' }}>
                    <div>
                        <FormItem name="notifyEnabled" valuePropName="checked" style={{ padding: 0, margin: 0 }}>
                            <Checkbox>Send a completion notification</Checkbox>
                        </FormItem>
                    </div>
                </div>
                {isCompletionNotificationEnabled && (
                    <ul style={{ ...bulletPointHelpStyling, marginTop: 10 }}>
                        <li>
                            Requires 'Checklist item completed' notification to be enabled in Notification settings.
                        </li>
                    </ul>
                )}
                <FormItem
                    style={{ paddingTop: 10 }}
                    label="Additional information"
                    name="description"
                    validateTrigger="onBlur"
                    rules={patternTokenSyntaxValidation()}
                >
                    <MarkdownEditor
                        autoFocus={false}
                        allowMentions
                        allowPatternedStrings
                        users={users}
                        userGroups={allGroups}
                    />
                </FormItem>
                <Text type="secondary" style={{ display: 'block', marginTop: -8, marginBottom: 16 }}>
                    Type @ to mention a user or group. Pattern token strings are also supported.{' '}
                    <Popover
                        content={
                            <div style={{ overflow: 'scroll', maxWidth: '40vw', maxHeight: '40vh' }}>
                                <PatternTokenHelperTextComponent availableTokens={AllPatternTokens} />
                            </div>
                        }
                        placement="top"
                    >
                        <InfoCircleOutlined style={{ color: antGrey.grey6 }} />
                    </Popover>
                </Text>
                {!!props.stepRunComponentProps && (
                    <div style={{ marginBottom: 16 }}>
                        <ChecklistItemImageUpload images={images} setImages={setImages} />
                    </div>
                )}
                <div style={{ marginBottom: 16 }}>
                    <FormItem
                        name="includeDescriptionInNotificationsEnabled"
                        valuePropName="checked"
                        style={{ marginBottom: 0 }}
                    >
                        <Checkbox disabled={(!description || description == '') && !images.length}>
                            {props.stepRunComponentProps ?
                                "Include 'Additional information' and images in notifications"
                            :   "Include 'Additional information' in notifications"}
                        </Checkbox>
                    </FormItem>
                </div>
                <div style={{ marginBottom: 16 }}>
                    <FormItem
                        name="disableReminderAfterReleaseComplete"
                        valuePropName="checked"
                        style={{ marginBottom: 0 }}
                    >
                        <Checkbox>Don't remind after release is complete</Checkbox>
                    </FormItem>
                </div>
                <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.CLIOwnership}>
                    {(isDisabled) => (
                        <>
                            <FormItem
                                label={
                                    <PlanRestrictedFeatureWrapper
                                        feature={SubscriptionFeatureType.CLIOwnership}
                                        requiresTooltip
                                    >
                                        <Text disabled={isDisabled}>Assign checklist item ownership by:</Text>
                                    </PlanRestrictedFeatureWrapper>
                                }
                                style={{ maxWidth: 320, marginBottom: 10 }}
                                name={'ownerType'}
                            >
                                <Radio.Group
                                    disabled={isDisabled}
                                    defaultValue={'approverGroups'}
                                    onChange={(e) => setOwnerType(e.target.value)}
                                >
                                    <Radio value={OwnerType.ApproverGroups}>User group</Radio>
                                    <Radio value={OwnerType.IndividualOwners}>User</Radio>
                                </Radio.Group>
                            </FormItem>
                            <FormItem
                                label={ownersLabel}
                                hidden={ownerType === OwnerType.IndividualOwners}
                                name={OwnerType.ApproverGroups}
                                style={{ maxWidth: 320 }}
                            >
                                <Select
                                    disabled={isDisabled}
                                    allowClear
                                    mode="multiple"
                                    placeholder="No owners"
                                    onChange={() => handleOwnerSelections(OwnerType.ApproverGroups)}
                                    filterOption={(input, option) => {
                                        const groupId = option?.value;
                                        if (!groupId) {
                                            return false;
                                        }
                                        return groupNameById[groupId]?.includes(input.toLowerCase()) ?? false;
                                    }}
                                >
                                    {ownerGroupOptions.map((groupOption) => {
                                        return (
                                            <Option value={groupOption.value} key={groupOption.value}>
                                                {groupOption.label}
                                            </Option>
                                        );
                                    })}
                                </Select>
                            </FormItem>
                            <FormItem
                                label={ownersLabel}
                                name={OwnerType.IndividualOwners}
                                hidden={ownerType === OwnerType.ApproverGroups}
                                style={{ maxWidth: 320 }}
                            >
                                <Select
                                    disabled={isDisabled}
                                    allowClear
                                    mode="multiple"
                                    filterSort={(optionA, optionB) => {
                                        const isSelectedA = userOwnerValues?.some(
                                            (selectedOption) => selectedOption === optionA.value,
                                        );
                                        const isSelectedB = userOwnerValues?.some(
                                            (selectedOption) => selectedOption === optionB.value,
                                        );

                                        return (
                                            isSelectedA && !isSelectedB ? -1
                                            : !isSelectedA && isSelectedB ? 1
                                            : 0
                                        );
                                    }}
                                    maxTagCount={'responsive'}
                                    placeholder="No owners"
                                    onChange={() => handleOwnerSelections(OwnerType.IndividualOwners)}
                                >
                                    {ownerUserOptions.map((userOption) => {
                                        return (
                                            <Option value={userOption.value} key={userOption.value}>
                                                {userOption.label}
                                            </Option>
                                        );
                                    })}
                                </Select>
                            </FormItem>
                        </>
                    )}
                </PlanRestrictedFeatureWrapper>
                {props.isOrgLevelTemplate ?
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Notifications about this checklist item will be sent to the primary channel for each app.
                    </Text>
                :   <FormItem
                        label={
                            <Space>
                                Set override notification channel(s)
                                <Tooltip title="Adding channel(s) here will override any existing configured channels: the primary notifications channel set in App settings > Integrations as well as any overrides configured from associated notification types in App settings > Notifications">
                                    <InfoCircleOutlined style={{ color: runwayColors.primary }} />
                                </Tooltip>
                            </Space>
                        }
                        name="channels"
                        style={{ maxWidth: 320 }}
                        initialValue={checklistItem?.channels}
                    >
                        <Select
                            mode="tags"
                            placeholder="Select"
                            allowClear
                            showSearch
                            filterOption={filterOptionLabel}
                            notFoundContent={null}
                            disabled={!notificationsProvider}
                        >
                            {availableChannels.map((channel, index) => (
                                <Option value={channel.id} key={index} label={channel.name}>
                                    {channel.name}
                                </Option>
                            ))}
                        </Select>
                    </FormItem>
                }
            </Form>
            <Space direction="vertical" size={'middle'}>
                {!isOneOffForVersion && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {helperText}
                    </Text>
                )}
                {isEditingItem?.uid && (
                    <Space>
                        <Text type="secondary">Checklist item identifier: </Text>
                        <Text
                            style={{
                                backgroundColor: antGrey.grey5,
                                color: runwayColors.darkTextGrey,
                                borderRadius: 3,
                                borderStyle: 'solid',
                                borderColor: antGrey.grey7,
                                borderWidth: 1,
                                padding: '2px 4px',
                            }}
                        >
                            {checklistItem?.templateId ? checklistItem.templateId : checklistItem.uid}
                        </Text>
                    </Space>
                )}
            </Space>
        </Modal>
    );
};

export default CreateUpdateChecklistItemModal;
